import {
  Log,
  LogLevel,
  StatsigClient,
  type StatsigOptions,
  type StatsigUser,
} from '@statsig/js-client';

import { env } from '@/env';
import { reportError } from '@/services/datadog/reporter';

const ERROR_PREFIX = 'Statsig';

export const TIER_REJECTED =
  'Statsig is off: VITE_STATSIG_CLIENT_KEY is set but the build mode is not development, staging or production.';

const TIER_DEVELOPMENT = 'development';
const TIER_STAGING = 'staging';
const TIER_PRODUCTION = 'production';

type StatsigTier = typeof TIER_DEVELOPMENT | typeof TIER_STAGING | typeof TIER_PRODUCTION;

const TIER_BY_MODE = new Map<string, StatsigTier>([
  [TIER_DEVELOPMENT, TIER_DEVELOPMENT],
  [TIER_STAGING, TIER_STAGING],
  [TIER_PRODUCTION, TIER_PRODUCTION],
]);

let client: StatsigClient | undefined;
let initialized = false;

const reported = new Set<string>();

const report = (summary: string): void => {
  if (reported.has(summary)) return;
  reported.add(summary);
  reportError(new Error(`${ERROR_PREFIX}: ${summary}`));
};

const errorLabel = (error: unknown): string => (error instanceof Error ? error.name : typeof error);

const buildUser = (): StatsigUser => ({ customIDs: {} });

const buildOptions = (tier: StatsigTier): StatsigOptions => ({
  environment: { tier },
  logLevel: import.meta.env.DEV ? LogLevel.Warn : LogLevel.None,
});

const disable = (): void => {
  Log.level = LogLevel.None;
};

export const initFeatureFlags = (): void => {
  if (initialized) return;

  const sdkKey = env.STATSIG_CLIENT_KEY?.trim();
  const tier = TIER_BY_MODE.get(env.MODE);
  if (!sdkKey || !tier) {
    if (sdkKey) console.warn(TIER_REJECTED);
    disable();
    return;
  }

  initialized = true;

  try {
    client = new StatsigClient(sdkKey, buildUser(), buildOptions(tier));
  } catch (error) {
    report(`client construction failed (${errorLabel(error)})`);
    disable();
    return;
  }

  client.on('error', ({ error, tag }) => report(`client error (${tag}: ${errorLabel(error)})`));
  client.on('initialization_failure', () => report('initialisation failed'));
  void client.initializeAsync();
};

export const getStatsigClient = (): StatsigClient | undefined => client;
